import { FC } from "react";

import styled from "@emotion/styled";
import {
  ANDROID_APP_DOWNLOAD_LINK,
  APP_DOWNLOAD_QR_URL,
  IOS_APP_DOWNLOAD_LINK
} from "src/constants/misc";
import translate from "src/lib/translate";
import appStore from "src/ui/assets/logos/app_store.png";
import googlePlay from "src/ui/assets/logos/google_play.png";
import Qrcode from "src/ui/components/Qrcode/Qrcode";
import Translate from "src/ui/components/Translate/Translate";
import type { TranslationKey } from "src/types/translationKey";

const QR_SIZE = 90;

const Box = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
`;

const QrSlot = styled.div`
  flex-shrink: 0;
  width: ${QR_SIZE}px;
`;

const Info = styled.div`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.5rem;
`;

const Text = styled.p`
  margin: 0;
  color: var(--color-charcoal, #212121);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  letter-spacing: -0.28px;
`;

const StoreBadges = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9.6px;
`;

const BadgeLink = styled.a`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    min-width: 44px;
    min-height: 44px;
  }
`;

const Badge = styled.img`
  height: 24px;
  width: auto;
  max-width: unset;
`;

interface MealAppDownloadBoxProps {
  /** Translation key for the copy next to the QR code. */
  msg: TranslationKey;
}

/**
 * Prompts the member to continue on mobile: a QR to the app download page plus
 * direct store links. Meal photo upload is app-only, so the desktop meal cards
 * offer this in place of an upload CTA.
 */
const MealAppDownloadBox: FC<MealAppDownloadBoxProps> = ({ msg }) => (
  <Box>
    <QrSlot>
      <Qrcode size={QR_SIZE} text={APP_DOWNLOAD_QR_URL} hideLogo />
    </QrSlot>
    <Info>
      <Text>
        <Translate msg={msg} />
      </Text>
      <StoreBadges>
        <BadgeLink
          href={IOS_APP_DOWNLOAD_LINK}
          target="_blank"
          rel="noreferrer"
        >
          <Badge src={appStore} alt={translate("image.alt.appStoreBadge")} />
        </BadgeLink>
        <BadgeLink
          href={ANDROID_APP_DOWNLOAD_LINK}
          target="_blank"
          rel="noreferrer"
        >
          <Badge
            src={googlePlay}
            alt={translate("image.alt.googlePlayBadge")}
          />
        </BadgeLink>
      </StoreBadges>
    </Info>
  </Box>
);

export default MealAppDownloadBox;
